"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { showcaseEntries, ShowcaseEntry } from "@/app/service/showcase/showcaseData";

const R2_BASE = "https://pub-6c4a08b7a6ba4982b5be067dec180888.r2.dev/brand-blog-images/changelyric";

interface HeroSlide {
  songName: string;
  description: string;
  clientDisplay: string;
  rating?: number;
  projectType: string;
  imageUrl: string;
  originalUrl: string;
  deliveredUrl: string;
}

const FEATURED_PER_TYPE = 6;

function buildSlides(): HeroSlide[] {
  const weddings = showcaseEntries.filter((e) => e.projectType === "Wedding").slice(0, FEATURED_PER_TYPE);
  const corporates = showcaseEntries.filter((e) => e.projectType === "Corporate").slice(0, FEATURED_PER_TYPE);
  const toSlide = (e: ShowcaseEntry): HeroSlide => ({
    songName: e.songName,
    description: e.description,
    clientDisplay: e.clientDisplay,
    rating: e.rating,
    projectType: e.projectType,
    imageUrl: e.heroImageUrl,
    originalUrl: `${R2_BASE}/showcase-audio/${e.slug}-original.mp3`,
    deliveredUrl: `${R2_BASE}/showcase-audio/${e.slug}-delivered.mp3`,
  });
  const slides: HeroSlide[] = [];
  const maxLen = Math.max(weddings.length, corporates.length);
  for (let i = 0; i < maxLen; i++) {
    if (weddings[i]) slides.push(toSlide(weddings[i]));
    if (corporates[i]) slides.push(toSlide(corporates[i]));
  }
  return slides;
}

const SLIDES = buildSlides();

function formatTime(seconds: number) {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function SlidePlayer({
  src,
  label,
  accent,
  onBecomeActive,
  onRelease,
}: {
  src: string;
  label: string;
  accent: "primary" | "secondary";
  onBecomeActive: (audio: HTMLAudioElement) => void;
  onRelease: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [playError, setPlayError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      if (isStarting) return;
      setPlayError(false);
      setIsStarting(true);
      try {
        await audio.play();
      } catch (err) {
        const name = err instanceof Error ? err.name : "";
        if (name !== "AbortError") setPlayError(true);
      } finally {
        setIsStarting(false);
      }
    }
  };

  // Warm only this slide's duration metadata so the seek bar and total time
  // are honest before first play. Deferred + idle-scheduled so hero audio
  // never competes with the page's initial image/connection storm, and
  // guarded so it can never restart an already-playing stream.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const kick = () => {
        if (cancelled) return;
        const a = audioRef.current;
        if (a && a.paused && a.readyState === 0) a.load();
      };
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(kick, { timeout: 3000 });
      } else {
        kick();
      }
    }, 1200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [src]);

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={() => {
          setIsPlaying(true);
          setPlayError(false);
          setIsStarting(false);
          if (audioRef.current) onBecomeActive(audioRef.current);
        }}
        onPause={() => {
          setIsPlaying(false);
          setIsStarting(false);
          onRelease();
        }}
        onEnded={() => {
          setIsPlaying(false);
          onRelease();
        }}
        onTimeUpdate={() => {
          if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
        }}
        onLoadedMetadata={() => {
          if (audioRef.current?.duration && isFinite(audioRef.current.duration)) {
            setDuration(audioRef.current.duration);
          }
        }}
        onError={() => {
          setPlayError(true);
          setIsStarting(false);
        }}
      />
      <button
        onClick={toggle}
        className={`btn btn-circle btn-sm ${accent === "primary" ? "btn-primary" : "btn-secondary"} flex-shrink-0`}
        aria-label={isPlaying ? `Pause ${label}` : `Play ${label}`}
        disabled={isStarting || playError}
      >
        {isStarting ? (
          <span className="loading loading-spinner loading-sm"></span>
        ) : isPlaying ? (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <span className={`badge badge-sm ${accent === "primary" ? "badge-primary" : "badge-secondary"} font-bold text-xs uppercase flex-shrink-0 w-[76px] justify-center text-center`}>
        {label}
      </span>
      {playError ? (
        <span className="text-xs text-error flex-1 max-sm:basis-full">Preview unavailable</span>
      ) : (
        <>
          {/* Mobile: single combined time reading pinned right; the seek bar wraps to its own full-width line below */}
          <span className="text-xs text-base-content/60 ml-auto whitespace-nowrap sm:hidden">
            {formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : "--:--"}
          </span>
          <span className="text-xs text-base-content/60 min-w-[34px] text-right max-sm:hidden">{formatTime(currentTime)}</span>
          <input
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={currentTime}
            disabled={duration <= 0}
            onChange={(e) => {
              const a = audioRef.current;
              if (!a) return;
              const t = parseFloat(e.target.value);
              a.currentTime = t;
              setCurrentTime(t);
            }}
            className={`range range-xs flex-1 min-w-0 max-sm:basis-full ${accent === "primary" ? "range-primary" : "range-secondary"}`}
            aria-label={`${label} seek`}
          />
          <span className="text-xs text-base-content/60 min-w-[34px] max-sm:hidden">{duration > 0 ? formatTime(duration) : "--:--"}</span>
        </>
      )}
    </div>
  );
}

export default function HeroShowcaseSlider() {
  const [index, setIndex] = useState(0);
  const [anyPlaying, setAnyPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const playingCount = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reducedMotion || anyPlaying || SLIDES.length < 2) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 9000);
    return () => clearInterval(t);
  }, [reducedMotion, anyPlaying]);

  const stopOthers = useCallback((current: HTMLAudioElement) => {
    containerRef.current?.querySelectorAll("audio").forEach((a) => {
      if (a !== current && !a.paused) {
        a.pause();
        a.dispatchEvent(new Event("pause"));
      }
    });
  }, []);

  const onBecomeActive = useCallback((audio: HTMLAudioElement) => {
    stopOthers(audio);
    playingCount.current += 1;
    setAnyPlaying(true);
  }, [stopOthers]);

  const onRelease = useCallback(() => {
    playingCount.current = Math.max(0, playingCount.current - 1);
    if (playingCount.current === 0) setAnyPlaying(false);
  }, []);

  const slide = SLIDES[index];

  return (
    <div ref={containerRef}>
      <style jsx>{`
        @keyframes heroSlideFade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-slide-panel {
          animation: heroSlideFade 0.5s ease-out;
        }
        .hero-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .hero-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-slide-panel { animation: none; }
        }
      `}</style>
      <div key={index} className="hero-slide-panel">
        <div className="flex items-center gap-4 my-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={slide.imageUrl}
            alt={slide.songName}
            className="w-20 h-20 rounded-lg object-cover shrink-0"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-nowrap">
              <span className={`badge badge-sm ${slide.projectType === "Wedding" ? "badge-secondary" : "badge-primary"} font-bold uppercase shrink-0`}>
                {slide.projectType}
              </span>
              {slide.rating ? (
                <span className="text-warning text-xs whitespace-nowrap shrink-0" aria-label={`${slide.rating} star review`}>
                  {"★".repeat(slide.rating)}
                </span>
              ) : null}
              <span className="text-xs text-base-content/50 truncate min-w-0">{slide.clientDisplay}</span>
            </div>
            <p className="font-semibold hero-clamp-1 mt-0.5">{slide.songName}</p>
            {/* wrappers own the responsive display:none — hero-clamp-* sets display:-webkit-box, which would fight them */}
            <div className="max-sm:hidden">
              <p className="text-sm text-base-content/70 hero-clamp-2">{slide.description}</p>
            </div>
          </div>
        </div>
        {/* Mobile: description gets the full card width instead of a sliver beside the image */}
        <div className="sm:hidden mb-2 -mt-1">
          <p className="text-sm text-base-content/70 hero-clamp-2">{slide.description}</p>
        </div>
        <div className="space-y-2">
          <SlidePlayer
            src={slide.originalUrl}
            label="Original"
            accent="primary"
            onBecomeActive={onBecomeActive}
            onRelease={onRelease}
          />
          <SlidePlayer
            src={slide.deliveredUrl}
            label="After"
            accent="secondary"
            onBecomeActive={onBecomeActive}
            onRelease={onRelease}
          />
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 mt-3">
        <button
          type="button"
          onClick={() => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length)}
          className="btn btn-circle btn-ghost btn-xs"
          aria-label="Previous example"
        >
          ‹
        </button>
        <div className="flex items-center gap-1.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.songName + i}
              type="button"
              onClick={() => setIndex(i)}
              className={`w-2 h-2 rounded-full transition-colors ${i === index ? "bg-primary" : "bg-base-300 hover:bg-base-content/30"}`}
              aria-label={`Go to example ${i + 1} of ${SLIDES.length}: ${s.songName}`}
              aria-current={i === index}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setIndex((i) => (i + 1) % SLIDES.length)}
          className="btn btn-circle btn-ghost btn-xs"
          aria-label="Next example"
        >
          ›
        </button>
        <span className="text-xs text-base-content/50">{index + 1} / {SLIDES.length}</span>
      </div>
    </div>
  );
}
